import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Accordion,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    BellIcon,
    UserIcon
} from "../../anatomy/icons";

const NAME = "Accordion";

const USAGE_CODE = `const [value, setValue] = useState<string | Array<string>>("cancel");

<Accordion
    variant="contained"
    onValueChange={setValue}
    value={value}
>
    <Accordion.Item
        value="cancel"
    >
        <Accordion.Trigger
            title="How can I cancel my subscription?"
        />
        <Accordion.Content>
            Go to Settings > Billing > Manage plan and press Cancel.
        </Accordion.Content>
    </Accordion.Item>
    <Accordion.Item
        value="invoice"
    >
        <Accordion.Trigger
            title="Where can I download my invoice?"
        />
        <Accordion.Content>
            The history list on the Billing page has a download link.
        </Accordion.Content>
    </Accordion.Item>
</Accordion>`;

const TRIGGER_CODE = `<Accordion.Item
    value="notifications"
>
    <Accordion.Trigger
        subTitle="2 of 3 notifications on"
        title="Notifications"
        icon={BellIcon}
        rightContent="On"
        headingLevel={3}
    />
    <Accordion.Content
        isInset={true}
    >
        ...
    </Accordion.Content>
</Accordion.Item>`;

const FAQ_ITEMS = [
    "cancel",
    "invoice",
    "payment"
] as const;

const ITEMS = [
    "first",
    "second",
    "third"
] as const;

const VARIANTS = [
    "plain",
    "contained",
    "card"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const MULTIPLE_VALUE = [
    "first",
    "second"
];

const PROFILE_VALUE = [
    "profile"
];

const CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("accordionPage.behaviourTitle"),
        id: "behaviour"
    },
    {
        title: t("accordionPage.triggerTitle"),
        id: "trigger"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const AccordionPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        openFaq,
        setOpenFaq
    ] = useState("cancel");

    const faqTitles = {
        invoice: t("accordionPage.faqInvoiceTitle"),
        payment: t("accordionPage.faqPaymentTitle"),
        cancel: t("accordionPage.faqCancelTitle")
    };

    const faqBodies = {
        invoice: t("accordionPage.faqInvoiceBody"),
        payment: t("accordionPage.faqPaymentBody"),
        cancel: t("accordionPage.faqCancelBody")
    };

    const itemTitles = {
        second: t("sample.item2"),
        third: t("sample.item3"),
        first: t("sample.item1")
    };

    const handleFaqChange = (value: string | Array<string>) => setOpenFaq(typeof value === "string" ? value : value[0] ?? "");

    const renderCell = (label: string, node: ReactNode) => <div
        style={CELL_STYLE}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </div>;

    const renderItems = () => ITEMS.map((item) => <Accordion.Item
        value={item}
        key={item}
    >
        <Accordion.Trigger
            title={itemTitles[item]}
        />
        <Accordion.Content>
            {t("sample.body")}
        </Accordion.Content>
    </Accordion.Item>);

    return <Article>
        <ComponentHeader
            lead={t("accordionPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("accordionPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("accordionPage.overviewCaption", [
                    JSON.stringify(openFaq)
                ])}
                isStacked={true}
            >
                <Accordion
                    onValueChange={handleFaqChange}
                    variant="contained"
                    value={openFaq}
                >
                    {
                        FAQ_ITEMS.map((item) => <Accordion.Item
                            value={item}
                            key={item}
                        >
                            <Accordion.Trigger
                                title={faqTitles[item]}
                            />
                            <Accordion.Content>
                                {faqBodies[item]}
                            </Accordion.Content>
                        </Accordion.Item>)
                    }
                </Accordion>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("accordionPage.variantsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    VARIANTS.map((variant) => renderCell(variant, <Accordion
                        defaultValue="first"
                        variant={variant}
                    >
                        {renderItems()}
                    </Accordion>))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("accordionPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => renderCell(size, <Accordion
                        defaultValue="first"
                        variant="contained"
                        size={size}
                    >
                        {renderItems()}
                    </Accordion>))
                }
            </Preview>
        </Section>
        <Section
            title={t("accordionPage.behaviourTitle")}
            id="behaviour"
        >
            <Paragraph>
                {t("accordionPage.behaviourText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderCell("expandBehaviour=\"single\"", <Accordion
                        defaultValue="first"
                        variant="contained"
                    >
                        {renderItems()}
                    </Accordion>)
                }
                {
                    renderCell("expandBehaviour=\"multiple\"", <Accordion
                        defaultValue={MULTIPLE_VALUE}
                        expandBehaviour="multiple"
                        variant="contained"
                    >
                        {renderItems()}
                    </Accordion>)
                }
                {
                    renderCell("isCollapsible={false}", <Accordion
                        isCollapsible={false}
                        defaultValue="first"
                        variant="contained"
                    >
                        {renderItems()}
                    </Accordion>)
                }
            </Preview>
        </Section>
        <Section
            title={t("accordionPage.triggerTitle")}
            id="trigger"
        >
            <Paragraph>
                {t("accordionPage.triggerText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Accordion
                    defaultValue={PROFILE_VALUE}
                    expandBehaviour="multiple"
                    variant="card"
                >
                    <Accordion.Item
                        value="profile"
                    >
                        <Accordion.Trigger
                            subTitle={t("sample.name")}
                            title={t("sample.item1")}
                            icon={UserIcon}
                        />
                        <Accordion.Content
                            isInset={true}
                        >
                            {t("sample.body")}
                        </Accordion.Content>
                    </Accordion.Item>
                    <Accordion.Item
                        value="notifications"
                    >
                        <Accordion.Trigger
                            subTitle={t("accordionPage.notificationsSub")}
                            title={t("accordionPage.notifications")}
                            rightContent={t("sample.stateOn")}
                            icon={BellIcon}
                        />
                        <Accordion.Content
                            isInset={true}
                        >
                            {t("sample.body")}
                        </Accordion.Content>
                    </Accordion.Item>
                </Accordion>
            </Preview>
            <CodeBlock
                code={TRIGGER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("accordionPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderCell("Accordion.Item isDisabled={true}", <Accordion
                        defaultValue="first"
                        variant="contained"
                    >
                        <Accordion.Item
                            value="first"
                        >
                            <Accordion.Trigger
                                title={t("sample.item1")}
                            />
                            <Accordion.Content>
                                {t("sample.body")}
                            </Accordion.Content>
                        </Accordion.Item>
                        <Accordion.Item
                            isDisabled={true}
                            value="second"
                        >
                            <Accordion.Trigger
                                title={t("sample.item2")}
                            />
                            <Accordion.Content>
                                {t("sample.body")}
                            </Accordion.Content>
                        </Accordion.Item>
                    </Accordion>)
                }
                {
                    renderCell("Accordion isDisabled={true}", <Accordion
                        defaultValue="first"
                        variant="contained"
                        isDisabled={true}
                    >
                        {renderItems()}
                    </Accordion>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("accordionPage.a11yHeading"),
                    t("accordionPage.a11yExpanded"),
                    t("accordionPage.a11yRegion"),
                    t("accordionPage.a11yKeyboard"),
                    t("accordionPage.a11yLocked"),
                    t("accordionPage.a11yDisabled"),
                    t("accordionPage.a11yIcon"),
                    t("accordionPage.a11yMotion"),
                    t("accordionPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"plain\" | \"contained\" | \"card\"",
                        description: t("accordionPage.propVariant"),
                        defaultValue: "\"plain\"",
                        name: "variant"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("accordionPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("accordionPage.propExpandBehaviour"),
                        type: "\"single\" | \"multiple\"",
                        defaultValue: "\"single\"",
                        name: "expandBehaviour"
                    },
                    {
                        description: t("accordionPage.propIsCollapsible"),
                        name: "isCollapsible",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("accordionPage.propValue"),
                        type: "string | Array<string>",
                        name: "value"
                    },
                    {
                        description: t("accordionPage.propDefaultValue"),
                        type: "string | Array<string>",
                        name: "defaultValue"
                    },
                    {
                        description: t("accordionPage.propOnValueChange"),
                        type: "(value: string | Array<string>) => void",
                        name: "onValueChange"
                    },
                    {
                        description: t("accordionPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("accordionPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("accordionPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Accordion.Item
                </InlineCode>
                {" "}
                {t("accordionPage.partItemText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("accordionPage.propItemValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("accordionPage.propItemIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("accordionPage.itemNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Accordion.Trigger
                </InlineCode>
                {" "}
                {t("accordionPage.partTriggerText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("accordionPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("accordionPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("accordionPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("accordionPage.propRightContent"),
                        name: "rightContent",
                        type: "ReactNode"
                    },
                    {
                        description: t("accordionPage.propHeadingLevel"),
                        type: "2 | 3 | 4 | 5 | 6",
                        name: "headingLevel",
                        defaultValue: "3"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("accordionPage.triggerNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Accordion.Content
                </InlineCode>
                {" "}
                {t("accordionPage.partContentText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("accordionPage.propIsInset"),
                        defaultValue: "false",
                        name: "isInset",
                        type: "boolean"
                    },
                    {
                        description: t("accordionPage.propIsKeepMounted"),
                        defaultValue: "false",
                        name: "isKeepMounted",
                        type: "boolean"
                    },
                    {
                        description: t("accordionPage.propContentRole"),
                        type: "Role",
                        name: "role"
                    },
                    {
                        description: t("accordionPage.propContentContainerStyle"),
                        name: "contentContainerStyle",
                        type: "StyleProp<ViewStyle>"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("accordionPage.contentNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default AccordionPage;
